import {
  Security as Shield,
  Cookie,
  Document as FileText,
  Locked as Lock,
  View as Eye,
  WarningAlt as AlertTriangle,
  type CarbonIconType as LucideIcon,
} from '@carbon/icons-react'

/**
 * Compliance copy, moved verbatim out of app/compliance/page.tsx. Not one
 * string was reworded. The page renders this and nothing else.
 */

export const COMPLIANCE = {
  badge: 'Legal & Compliance',
  title: 'Compliance',
  deck: 'We take privacy, security, and transparency seriously. Everything about how we handle your data and operate responsibly in one place.',

  policiesTitle: 'Our Policies',
  policiesDeck: 'Full policy documents governing euclideum.com.',
  policiesNote: 'All updated Feb 28, 2026',

  standardsTitle: 'Security & Standards',
  standardsDeck: 'How we protect your data and operate responsibly.',

  rightsTitle: 'Your Rights',
  rightsDeck:
    'Depending on your location and applicable law, you may have the following rights regarding your personal data.',
  /* Substantive guidance — it qualifies the six rights above and must never be
     dropped when this section is restyled. */
  rightsNoteBefore: 'To exercise any of these rights, contact us at ',
  rightsNoteAfter:
    '. Some rights may be limited where we have a lawful basis to continue processing.',

  /* Hero entity + jurisdiction strip. */
  jurisdiction: 'Maharashtra, India',

  contactTitle: 'Questions about compliance?',
  contactDeck: 'Questions about our policies or want to exercise your rights.',
  contactEmail: 'hq@euclideum.com',
  entity: 'Euclideum Solutions Pvt. Ltd.',
  address: '203A, Shiv Heights, Ambedkar Chowk, Thane West, 400601',

  noticeLabel: 'Notice',
  notice:
    'All policies were last updated February 28, 2026 and are governed by the laws of Maharashtra, India. This page should be read alongside the individual policy documents linked above.',
} as const

export const POLICIES: {
  icon: LucideIcon
  title: string
  description: string
  href: string
  badge: string
}[] = [
  {
    icon: Eye,
    title: 'Privacy Policy',
    description:
      'How we collect, use, store, and protect your personal data. Covers data retention, third-party sharing, your rights, and how to contact us.',
    href: '/privacy',
    badge: 'Feb 28, 2026',
  },
  {
    icon: Cookie,
    title: 'Cookies Policy',
    description:
      'What cookies we use, why we use them, and how you can manage or disable them through your browser settings at any time.',
    href: '/cookies',
    badge: 'Feb 28, 2026',
  },
  {
    icon: FileText,
    title: 'Terms & Conditions',
    description:
      'The agreement governing your use of euclideum.com your rights and obligations, limitations of liability, and dispute resolution.',
    href: '/terms',
    badge: 'Feb 28, 2026',
  },
]

export const STANDARDS: {
  icon: LucideIcon
  title: string
  description: string
}[] = [
  {
    icon: Lock,
    title: 'Data Security',
    description:
      'Commercially reasonable technical and organisational measures protect your data from unauthorised access. All data is transmitted over encrypted TLS connections.',
  },
  {
    icon: Shield,
    title: 'Data Minimisation',
    description:
      'We collect only what is necessary to provide our services and anonymise or delete data once it is no longer needed.',
  },
  {
    icon: Eye,
    title: 'Transparency',
    description:
      'Our Privacy Policy is written in plain language. We are open about what we collect, why, and how long we keep it.',
  },
  {
    icon: AlertTriangle,
    title: 'Responsible Disclosure',
    description:
      'Found a security vulnerability? Report it to hq@euclideum.com. We take all valid reports seriously and address them promptly.',
  },
]

export const RIGHTS: { right: string; detail: string }[] = [
  {
    right: 'Access',
    detail: 'Request a copy of the personal data we hold about you.',
  },
  {
    right: 'Correction',
    detail: 'Ask us to correct inaccurate or incomplete data.',
  },
  {
    right: 'Deletion',
    detail: 'Request deletion where we have no lawful basis to retain it.',
  },
  {
    right: 'Objection',
    detail: 'Object to processing for direct marketing or other purposes.',
  },
  {
    right: 'Portability',
    detail: 'Receive your data in a structured, machine-readable format.',
  },
  {
    right: 'Withdrawal',
    detail:
      'Withdraw consent at any time where processing is based on consent.',
  },
]
